<script setup lang="ts">
import { computed } from 'vue';

import type { ResolutionEvent, ResolutionMessage } from '@/library/message/types';
import type { ResolutionKind, ResolutionMessageType } from '@/library/message/enum';
import { MessageType, SystemEvent } from '@/library/message/enum';

import ChatMessageWrapper from '../ChatMessageWrapper.vue';
import MessageActions from '../../MessageActions.vue';

interface ActionItem {
  id: string | number;
  label?: string;
  icon?: string;
  selected?: boolean;
  disabled?: boolean;
  rightIcon?: string;
  imageUri?: string;
}

import {
  DEFAULT_RESOLUTION_OPTIONS_ORDER,
  RESOLUTION_OPTIONS_ORDER,
  RESOLUTION_OPTIONS_POSITION,
  RESOLUTION_OPTIONS_TEXT,
} from './ResolutionMessage.config';

export interface ResolutionMessageProps {
  message: ResolutionMessage;
  /**
   * The previous message/notice in the stack.
   */
  previous?: unknown;
  /**
   * The next message/notice in the stack.
   */
  next?: unknown;
}

const props = defineProps<ResolutionMessageProps>();
const emit = defineEmits<{
  (e: 'message:input', payload: ResolutionEvent): void;
}>();

const resolutionOptionText = computed(() => {
  const messageType = props.message.type as unknown as ResolutionMessageType;
  const options = RESOLUTION_OPTIONS_TEXT[messageType];
  return options || RESOLUTION_OPTIONS_TEXT[MessageType.RESOLUTION]!;
});

const resolutionOptionsPosition = computed(() => {
  const messageType = props.message.type as unknown as ResolutionMessageType;
  const position = RESOLUTION_OPTIONS_POSITION[messageType];
  return position || RESOLUTION_OPTIONS_POSITION[MessageType.RESOLUTION]!;
});

const resolutionOptionsOrder = computed(() => {
  const messageType = props.message.type as ResolutionMessageType;
  return RESOLUTION_OPTIONS_ORDER[messageType] ?? DEFAULT_RESOLUTION_OPTIONS_ORDER;
});

const actionItems = computed<ActionItem[]>(() => {
  const selectedOption = props.message.selectedOption;
  return resolutionOptionsOrder.value.map((option) => ({
    id: option,
    label: resolutionOptionText.value[option],
    selected: selectedOption === option,
  }));
});

const submitResolution = (item: ActionItem) => {
  if (!!props.next) return;

  const selected = item.id as ResolutionKind;
  const selectedText = resolutionOptionText.value[selected];

  const payload: ResolutionEvent = {
    type: SystemEvent.USER_RESOLUTION_SELECTED,
    message: props.message,
    payload: { selected, selectedText },
  };

  emit('message:input', payload);
};

const containerClass = computed(() => `resolution-options ${resolutionOptionsPosition.value}`);
</script>

<template>
  <ChatMessageWrapper :previous="previous" :next="next" :message="message">
    <template #actions>
      <MessageActions
        :items="actionItems"
        :container-class="containerClass"
        data-testid="resolution-actions"
        :data-message-type="message.type"
        @select="submitResolution"
      />
    </template>
  </ChatMessageWrapper>
</template>

<style lang="scss" scoped>
:deep(.resolution-options) {
  display: flex;
  justify-content: center;
  gap: 8px;
}

:deep(.resolution-options.flex-row) {
  flex-direction: row;
}

:deep(.resolution-options.flex-col) {
  flex-direction: column;
}

/* Style the action items to look like buttons */
:deep(.resolution-options .item) {
  flex: 0 1 auto;
  min-width: 80px;
  width: 100%;
  padding: 8px 16px;
  text-align: center;
  justify-content: center;
  transition: all 0.2s ease;
}

:deep(.resolution-options .item:hover) {
  opacity: 0.8;
}

:deep(.resolution-options .item-text) {
  padding: 0;
}
</style>
